import { useEffect, useMemo, useState } from 'react';
import type {
  ControlStatus,
  GapAnswer,
  OnboardingAnswers,
  PolicyStatus,
  TrustFamily,
} from '@wedpo/soc2';
import {
  DEMO_COMPANY,
  controls,
  onboardingComplete,
  preworkChecklist,
  readinessPct,
  seedAssessedControls,
} from '@wedpo/soc2';
import {
  MOCK_PHASES,
  PUBLIC_FREE,
  computeMockPhaseStatus,
  computePhaseStatus,
  mockPhaseForView,
  useEditorMode,
  useLocalStorage,
  useScrollTopOnChange,
  type ViewId,
} from '@/lib/store';
import { SlashMark } from '@/components/common';
import { KitProposalModal } from '@/components/KitProposalModal';
import { Overview } from '@/views/Overview';
import { Evidence } from '@/views/Evidence';
import { Journey } from '@/views/Journey';
import { SystemDescription } from '@/views/SystemDescription';
import { Policies } from '@/views/Policies';
import { GapAnalysis } from '@/views/GapAnalysis';
import { Phase1 } from '@/views/Phase1';
import { Implementation } from '@/views/Implementation';
import { P0, P3, P5, P6 } from '@/views/PhasePages';

interface NavItem {
  v: ViewId;
  ix: string;
  label: string;
}
interface NavGroup {
  h: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    h: 'Project',
    items: [{ v: 'info', ix: 'i', label: 'Overview' }],
  },
  {
    h: 'Documents',
    items: [
      { v: 'sysdesc', ix: '§', label: 'System description' },
      { v: 'policies', ix: '¶', label: 'Policies' },
    ],
  },
  {
    h: 'The journey',
    items: MOCK_PHASES.map((p) => ({
      v: p.primary,
      ix: String(Number(p.num)),
      label: p.name,
    })),
  },
];

export default function App() {
  const [view, setView] = useLocalStorage<ViewId>('view', 'info');
  const [scopeArr, setScopeArr] = useLocalStorage<TrustFamily[]>('scope', [
    'CC',
    'A',
    'C',
    'P',
  ]);
  const [sdAnswers, setSdAnswers] = useLocalStorage<Record<string, string>>(
    'sysdesc',
    {},
  );
  const [tokenValues, setTokenValues] = useLocalStorage<
    Record<string, Record<string, string>>
  >('policyTokens', {});
  const [statusOverrides, setStatusOverrides] = useLocalStorage<
    Record<string, PolicyStatus>
  >('policyStatus', {});
  const [onbAnswers, setOnbAnswers] = useLocalStorage<OnboardingAnswers>(
    'onboarding',
    {},
  );
  const [prework, setPrework] = useLocalStorage<string[]>('prework', []);
  // Phase 2 gap analysis: the owner's plain-language answers, gap notes, and
  // evidence filenames per control. Evidence files are simulated for now: we
  // record the file name in localStorage; real Storage lands once auth exists.
  const [controlAnswers, setControlAnswers] = useLocalStorage<
    Record<string, GapAnswer>
  >('controlAnswers', {});
  const [controlNotes, setControlNotes] = useLocalStorage<Record<string, string>>(
    'controlNotes',
    {},
  );
  const [controlEvidence, setControlEvidence] = useLocalStorage<
    Record<string, string[]>
  >('controlEvidence', {});

  const isEditor = useEditorMode();
  const [proposalFor, setProposalFor] = useState<string | null>(null);

  // The Phase 2 answers override the seeded demo state so the dashboard,
  // readiness, and Phase 3 plan all reflect what the owner actually rated.
  // "unsure" is not a real state, so it maps to N/A and stays out of readiness.
  const assessed = useMemo(
    () =>
      seedAssessedControls().map((c) => {
        const a = controlAnswers[c.code];
        if (!a) return c;
        const status: ControlStatus = a === 'unsure' ? 'na' : a;
        return { ...c, status };
      }),
    [controlAnswers],
  );
  const readiness = useMemo(() => readinessPct(assessed), [assessed]);

  // Controls in scope = those touching at least one selected trust family.
  const inScope = useMemo(
    () => controls.filter((c) => c.trustFamilies.some((f) => scopeArr.includes(f))),
    [scopeArr],
  );

  // Progressive unlock: Phase 1 is done when onboarding + pre-work are complete;
  // Phase 2 is done once every in-scope control has been rated.
  const preworkDone = preworkChecklist.every((i) => prework.includes(i.id));
  const p1Done = onboardingComplete(onbAnswers) && preworkDone;
  // Phase 2 is complete once every in-scope control is rated. "Doesn't apply"
  // is the one rating that also needs a written reason, so it only counts when
  // a note is present (the gap card shows a warning until one is given).
  const p2Done =
    inScope.length > 0 &&
    inScope.every((c) => {
      const a = controlAnswers[c.code];
      if (!a) return false;
      if (a === 'na') return Boolean((controlNotes[c.code] ?? '').trim());
      return true;
    });
  const phaseStatus = useMemo(
    () => computePhaseStatus(p1Done, p2Done),
    [p1Done, p2Done],
  );
  const mockStatus = useMemo(
    () => computeMockPhaseStatus(phaseStatus),
    [phaseStatus],
  );

  useScrollTopOnChange(view);

  // A public visitor who lands on a gated view gets bounced to Overview.
  useEffect(() => {
    if (!isEditor && !PUBLIC_FREE.has(view)) setView('info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor]);

  // Navigation gate: for the public, any paid phase opens the kit proposal form
  // instead of navigating. Editors pass straight through to everything.
  const go = (v: ViewId) => {
    if (!isEditor && !PUBLIC_FREE.has(v)) {
      const ph = MOCK_PHASES.find((p) => p.primary === v);
      setProposalFor(ph ? ph.name : 'This section');
      return;
    }
    setView(v);
  };

  const primaryToPhase = (v: ViewId) => MOCK_PHASES.find((m) => m.primary === v);
  const navDone = (v: ViewId) => {
    const m = primaryToPhase(v);
    return m ? mockStatus[m.id] === 'done' : false;
  };
  const navLocked = (v: ViewId) => {
    const m = primaryToPhase(v);
    if (!m) return false;
    return isEditor ? mockStatus[m.id] === 'locked' : !PUBLIC_FREE.has(m.primary);
  };
  const navActive = (v: ViewId) => {
    const m = primaryToPhase(v);
    if (m) return mockPhaseForView(view) === m.id;
    return view === v;
  };

  const toggleFam = (f: TrustFamily) => {
    if (f === 'CC') return;
    setScopeArr((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f],
    );
  };

  const setTokenValue = (pid: string, tid: string, value: string) =>
    setTokenValues((prev) => ({ ...prev, [pid]: { ...prev[pid], [tid]: value } }));

  const adopt = (pid: string) =>
    setStatusOverrides((prev) => ({ ...prev, [pid]: 'adopted' }));

  const togglePrework = (id: string) =>
    setPrework((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const rateControl = (code: string, answer: GapAnswer) =>
    setControlAnswers((prev) => ({ ...prev, [code]: answer }));

  const noteControl = (code: string, note: string) =>
    setControlNotes((prev) => ({ ...prev, [code]: note }));

  // Simulated evidence: record the file names only (no backend storage yet).
  // A control can carry several files; we append new ones and skip duplicates.
  const evidenceList = (v: unknown): string[] =>
    Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : [];

  const attachEvidence = (code: string, fileNames: string[]) =>
    setControlEvidence((prev) => {
      const merged = evidenceList(prev[code]);
      for (const n of fileNames) if (!merged.includes(n)) merged.push(n);
      return { ...prev, [code]: merged };
    });

  const removeEvidence = (code: string, fileName: string) =>
    setControlEvidence((prev) => {
      const next = evidenceList(prev[code]).filter((n) => n !== fileName);
      const out = { ...prev };
      if (next.length) out[code] = next;
      else delete out[code];
      return out;
    });

  const scope = new Set(scopeArr);

  return (
    <>
      <div className="topbar">
        <button
          type="button"
          onClick={() => go('info')}
          aria-label="Back to overview"
          title="Overview"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: 'none',
            border: 0,
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <SlashMark />
        </button>
        <span className="crumb">SOC 2 Readiness</span>
        <div className="spacer" />
        {isEditor && <span className="chip-pill chip-editor">Editor preview</span>}
        <span className="chip-pill">{DEMO_COMPANY}</span>
        <span
          className={`chip-pill chip-ready ${
            readiness >= 80 ? 'tone-green' : readiness >= 50 ? 'tone-amber' : 'tone-red'
          }`}
        >
          <span className="dot" /> Readiness <strong>{readiness}%</strong>
        </span>
      </div>

      <div className="layout">
        <nav className="rail">
          {NAV.map((group) => (
            <div key={group.h}>
              <h4 style={group.h === 'Project' ? undefined : { marginTop: '1.25rem' }}>
                {group.h}
              </h4>
              {group.items.map((item) => (
                <button
                  key={item.v}
                  className={`navItem${navActive(item.v) ? ' active' : ''}`}
                  onClick={() => go(item.v)}
                >
                  <span className={`ix${navDone(item.v) ? ' done' : ''}`}>{item.ix}</span>{' '}
                  {item.label}
                  {navLocked(item.v) && <span className="lk">🔒</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <main>
          {view === 'info' && (
            <Overview
              company={DEMO_COMPANY}
              readiness={readiness}
              mockStatus={mockStatus}
              assessed={assessed}
              answers={controlAnswers}
              evidence={controlEvidence}
              scope={scope}
              toggleFam={toggleFam}
              go={go}
            />
          )}
          {view === 'evidence' && (
            <Evidence
              inScope={assessed.filter((c) =>
                c.trustFamilies.some((f) => scope.has(f)),
              )}
              evidence={controlEvidence}
              attachEvidence={attachEvidence}
              removeEvidence={removeEvidence}
            />
          )}
          {view === 'journey' && <Journey phaseStatus={phaseStatus} />}
          {view === 'sysdesc' && (
            <SystemDescription answers={sdAnswers} setAnswers={setSdAnswers} />
          )}
          {view === 'policies' && (
            <Policies
              tokenValues={tokenValues}
              setTokenValue={setTokenValue}
              statusOverrides={statusOverrides}
              adopt={adopt}
            />
          )}
          {view === 'p0' && <P0 go={go} />}
          {view === 'p1' && (
            <Phase1
              answers={onbAnswers}
              setAnswers={setOnbAnswers}
              prework={prework}
              togglePrework={togglePrework}
              phaseStatus={phaseStatus}
              p1Done={p1Done}
              go={go}
            />
          )}
          {view === 'p2' && (
            <GapAnalysis
              inScope={inScope}
              answers={controlAnswers}
              notes={controlNotes}
              evidence={controlEvidence}
              rateControl={rateControl}
              noteControl={noteControl}
              attachEvidence={attachEvidence}
              removeEvidence={removeEvidence}
              p2Done={p2Done}
              go={go}
            />
          )}
          {view === 'p3' && <P3 assessed={assessed} />}
          {view === 'p4' && <Implementation go={go} />}
          {view === 'p5' && <P5 />}
          {view === 'p6' && <P6 />}

          <p className="foot">
            WeDPO · SOC 2 kit · live data: 87 controls from the QCA register
          </p>
        </main>
      </div>

      {proposalFor && (
        <KitProposalModal
          section={proposalFor}
          onClose={() => setProposalFor(null)}
        />
      )}
    </>
  );
}
